import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Observable, Subject, of, takeUntil, map, catchError } from 'rxjs';
import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../shared/services/user-directory-api.service';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';

import { WorkflowApiService } from '../services/workflow-api.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { WorkflowFolderApiService } from '../services/workflow-folder-api.service';
import { WorkflowBuilderApiService } from '../services/workflow-builder-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import {
  workflowNodeSubtypeLabel,
  workflowNodeTypeLabel,
  workflowObjectTypeLabel
} from '../models/workflow-labels';
import {
  WorkflowResponse,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  WorkflowFolderResponse,
  WorkflowObjectType,
  WORKFLOW_OBJECT_TYPES_ENABLED,
  WORKFLOW_OBJECT_TYPE_LABELS,
  WorkflowExitCriteriaRule,
  WorkflowSuppressionRule,
  WorkflowRuleOperator,
  WORKFLOW_RULE_OPERATOR_LABELS,
  WORKFLOW_STATUS_LABELS
} from '../models/workflow.model';
import { WorkflowNodeResponse } from '../models/workflow-builder.model';
import { WorkflowPropertySelectComponent } from '../workflow-builder/components/inspector/node-config/property-select.component';
import { WorkflowConditionValueComponent } from '../workflow-builder/components/inspector/node-config/condition-value.component';
import {
  buildPropertyOptions,
  findPropertyDescriptor,
  WorkflowPropertyOption
} from '../workflow-builder/models/workflow-variable-catalog';
import {
  conditionOperatorOptionsFor,
  CONDITION_OPERATOR_OPTIONS
} from '../workflow-builder/components/inspector/node-config/config-options';

@Component({
  selector: 'app-workflow-form',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, EntitySelectComponent, WorkflowPropertySelectComponent, WorkflowConditionValueComponent],
  templateUrl: './workflow-form.component.html'
})
export class WorkflowFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isEditing = false;
  workflowId: number | null = null;
  saving = false;
  loading = false;
  error = '';

  folders: WorkflowFolderResponse[] = [];
  tagInput = '';

  // Responsable (inc 2) — buscador por nombre/correo
  ownerName = '';

  // Sección avanzada (inc 1) — solo visible/editable en modo edición
  showAdvanced = false;

  /**
   * INC_CRM_020 (6) inc15 — estado del workflow que se está editando. Un workflow ACTIVO ya no
   * rebota entero ("Solo se pueden editar workflows en estado Borrador o Pausado"): la ficha se
   * edita en caliente y solo se congela lo que redefine la ejecución. Se guarda aquí para poder
   * bloquear esos campos en pantalla en vez de dejar que el usuario los toque y coma un 409.
   */
  workflowStatus: string | null = null;

  get lockedByActive(): boolean {
    return this.workflowStatus === 'ACTIVE';
  }

  // Modal de plantillas
  showTemplatesModal = false;
  templates: WorkflowResponse[] = [];
  templatesLoading = false;

  // Selector de nodos para re-inscripción FROM_NODE
  availableNodes: WorkflowNodeResponse[] = [];
  nodesLoading = false;

  // Exit criteria (gestionado fuera del FormGroup como arrays dinámicos)
  exitCriteriaRules: WorkflowExitCriteriaRule[] = [];
  showAddExitCriteria = false;
  editingExitCriteriaIndex: number | null = null;
  draftExitCriteria = this.emptyExitCriteriaDraft();
  draftExitCriteriaError = '';

  // Suppression rules
  suppressionRulesArr: WorkflowSuppressionRule[] = [];
  showAddSuppression = false;
  editingSuppressionIndex: number | null = null;
  draftSuppression = this.emptySuppressionDraft();
  draftSuppressionError = '';

  // A4: buscador del registro a suprimir por nombre (según draftSuppression.objectType).
  suppressionRecordSelect!: WorkflowRecordSelect;
  draftSuppressionObjectName = '';

  readonly OPERATORS: WorkflowRuleOperator[] = [
    'EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS',
    'IS_EMPTY', 'IS_NOT_EMPTY', 'GREATER_THAN', 'LESS_THAN', 'IN', 'NOT_IN'
  ];
  readonly OPERATOR_LABELS = WORKFLOW_RULE_OPERATOR_LABELS;

  /**
   * INC_CRM_020 (9) inc47 — el desplegable mostraba «Ticket» DOS VECES: se añadía a mano cuando
   * TICKET todavía no estaba habilitado y al habilitarlo entró también en
   * `WORKFLOW_OBJECT_TYPES_ENABLED`, así que la lista lo pintaba dos veces. La lista habilitada es
   * la única fuente.
   */
  readonly objectTypeOptions: { value: WorkflowObjectType; label: string; disabled: boolean }[] =
    WORKFLOW_OBJECT_TYPES_ENABLED.map(t => ({ value: t, label: WORKFLOW_OBJECT_TYPE_LABELS[t], disabled: false }));

  /** Tipos de objeto seleccionables para las reglas de salida/supresión (los habilitados). */
  readonly ruleObjectTypeOptions: { value: WorkflowObjectType; label: string }[] =
    WORKFLOW_OBJECT_TYPES_ENABLED.map(t => ({ value: t, label: WORKFLOW_OBJECT_TYPE_LABELS[t] }));

  /** ¿El valor de tipo de objeto está entre los habilitados? (para conservar valores heredados). */
  isEnabledObjectType(value: string): boolean {
    return WORKFLOW_OBJECT_TYPES_ENABLED.includes(value as WorkflowObjectType);
  }

  /** Propiedades sugeridas del objeto (clave legible) para el selector de "Campo". Cae a texto libre. */
  propertyOptionsFor(objectType: string | null | undefined): WorkflowPropertyOption[] {
    return buildPropertyOptions((objectType || null) as WorkflowObjectType | null);
  }

  /**
   * El editor de valor emite el tipo nativo de la propiedad (número al elegir un estado del
   * catálogo, id al elegir un registro); estas reglas lo guardan como texto, que es lo que declara
   * `WorkflowExitCriteriaRule.value` y lo que compara el backend (normaliza texto y número).
   */
  asText(value: unknown): string {
    return value == null ? '' : String(value);
  }

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private workflowApi: WorkflowApiService,
    private folderApi: WorkflowFolderApiService,
    private builderApi: WorkflowBuilderApiService,
    private authService: AuthService,
    private userDirectory: UserDirectoryApiService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  // ─── Buscador de usuario por nombre/correo (server-side, sobre el directorio de la empresa) ───
  // El directorio solo exige estar autenticado; /api/auth/users/search exigía `users:VIEW` y
  // dejaba el buscador vacío para todo rol que no fuera Super Admin, Admin o Manager.
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.userDirectory.searchUsers(term, 20).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );

  userLabel = (u: DirectoryUser): string => directoryUserLabel(u);
  userSublabel = (u: DirectoryUser): string => u.mail ?? '';

  onOwnerSelected(u: DirectoryUser | null): void {
    this.ownerName = u ? this.userLabel(u) : '';
  }

  /**
   * Resuelve el nombre del responsable a partir de su id (al editar). Por ids el directorio
   * devuelve también a los INACTIVOS: un workflow cuyo responsable ya no está activo sigue
   * mostrando su nombre.
   */
  private resolveOwnerName(id: number): void {
    this.userDirectory.fetchUsersByIds([id]).pipe(takeUntil(this.destroy$)).subscribe({
      next: (users) => {
        const u = users.find((x) => x.id === id);
        if (u) this.ownerName = this.userLabel(u);
      },
      error: () => {}
    });
  }

  /**
   * INC_CRM_020 (6) inc15 — congela los campos que el backend rechaza mientras el workflow está
   * ACTIVO. Es exactamente la misma lista que `WorkflowService.lockedFieldsWhileActive`: el tipo
   * de objeto base (cambiarlo deja huérfanas las inscripciones existentes), la configuración de
   * inscripción / salida / supresión y el tope de inscripciones activas (reparten el tráfico a
   * mitad de ejecución). Todo lo demás — nombre, descripción, carpeta, etiquetas, responsable —
   * queda editable, que es justo lo que pedía el incidente.
   */
  private lockExecutionFields(): void {
    const locked = [
      'objectType', 'maxActiveEnrollments',
      'allowReEnrollment', 'reEnrollmentStartMode', 'reEnrollmentStartNodeId',
      'rampUpEnabled', 'rampUpPercentage',
      'monitoringErrorAlertsEnabled', 'monitoringNotifyOwner', 'monitoringNotifyAdmins',
      'monitoringErrorThreshold', 'monitoringErrorRateThreshold',
      // Vive en settings_json, y `lockedFieldsWhileActive` rechaza CUALQUIER cambio de settings
      // mientras el workflow está ACTIVO: sin congelarlo aquí, tocarlo daría un 400 al guardar.
      'allowDangerousActions'
    ];
    for (const name of locked) {
      this.form.get(name)?.disable({ emitEvent: false });
    }
  }

  ngOnInit(): void {
    this.initForm();

    // A4: helper reutilizable — busca el registro a suprimir por nombre según el objectType del draft.
    this.suppressionRecordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.draftSuppression.objectType,
      () => this.authService.getCurrentUser()?.businessId || this.businessId || 1
    );

    const id = this.route.snapshot.paramMap.get('id');
    if (id && !isNaN(Number(id))) {
      this.isEditing = true;
      this.workflowId = Number(id);
      this.loadWorkflow(this.workflowId);
    } else {
      // Al crear, el responsable por defecto es el usuario actual (editable).
      const current = this.authService.getCurrentUser();
      if (current) {
        this.ownerName = `${current.fullname ?? ''}${current.lastname ? ' ' + current.lastname : ''}`.trim() || (current.email ?? '');
      }
    }

    this.loadFolders();

    const templateId = this.route.snapshot.queryParamMap.get('templateId');
    if (templateId && !this.isEditing) {
      this.loadFromTemplate(Number(templateId));
    }

    // Cargar nodos automáticamente al cambiar a FROM_NODE
    this.form.get('reEnrollmentStartMode')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        if (mode === 'FROM_NODE') {
          this.loadNodes();
        } else {
          this.form.patchValue({ reEnrollmentStartNodeId: null });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  /**
   * INC_CRM_020 (2) inc5 — mismo tope que `@Size(max = 200)` de `WorkflowCreateRequest`
   * y `WorkflowUpdateRequest`. Se expone a la plantilla para el atributo `maxlength` del
   * input y el contador, de modo que el límite se vea antes de intentar guardar en vez
   * de descubrirse con un error al enviar.
   */
  readonly NAME_MAX_LENGTH = 200;

  /**
   * INC_CRM_020 (2) inc6 — tope de la descripción, alineado con el `@Size(max = 5000)` que
   * ahora tienen `WorkflowCreateRequest` y `WorkflowUpdateRequest`. Antes no había límite en
   * ninguna capa (la columna es TEXT), así que se podían guardar descripciones de cualquier
   * tamaño y luego la vista detalle tenía que lidiar con ellas.
   */
  readonly DESCRIPTION_MAX_LENGTH = 5000;

  private initForm(): void {
    this.form = this.fb.group({
      name:                    ['', [Validators.required, Validators.maxLength(this.NAME_MAX_LENGTH)]],
      description:             ['', [Validators.maxLength(this.DESCRIPTION_MAX_LENGTH)]],
      objectType:              [null, Validators.required],
      idFolder:                [null],
      idOwnerUser:             [this.authService.getCurrentUser()?.id ?? null],
      tags:                    [[]],
      allowReEnrollment:       [false],
      reEnrollmentStartMode:   ['FROM_START'],
      reEnrollmentStartNodeId: [null],
      maxActiveEnrollments:    [null, [Validators.min(1)]],
      priorityOrder:           [0],
      rampUpEnabled:                  [false],
      rampUpPercentage:               [null, [Validators.min(1), Validators.max(100)]],
      monitoringErrorAlertsEnabled:   [false],
      monitoringNotifyOwner:          [true],
      monitoringNotifyAdmins:         [false],
      monitoringErrorThreshold:       [null, [Validators.min(1)]],
      monitoringErrorRateThreshold:   [null, [Validators.min(1), Validators.max(100)]],
      // Interruptor que exige el backend para ejecutar «Eliminar registro»
      // (WorkflowCrmActionService: settings_json.allowDangerousActions). No existía en ninguna
      // pantalla, así que ese nodo SIEMPRE fallaba con un mensaje que nombraba la clave del JSON.
      allowDangerousActions:          [false]
    });
  }

  private loadWorkflow(id: number): void {
    this.loading = true;
    this.workflowApi.getById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (workflow) => {
        const settings = workflow.settings ?? {};
        this.form.patchValue({
          name:                    workflow.name,
          description:             workflow.description ?? '',
          objectType:              workflow.objectType,
          idFolder:                workflow.idFolder ?? null,
          idOwnerUser:             workflow.idOwnerUser ?? null,
          tags:                    workflow.tags ?? [],
          allowReEnrollment:       settings['allowReEnrollment'] ?? workflow.reEnrollmentEnabled ?? false,
          reEnrollmentStartMode:   settings['reEnrollmentStartMode'] ?? 'FROM_START',
          reEnrollmentStartNodeId: settings['reEnrollmentStartNodeId'] ?? null,
          maxActiveEnrollments:    workflow.maxActiveEnrollments ?? null,
          priorityOrder:           workflow.priorityOrder ?? 0,
          allowDangerousActions:   settings['allowDangerousActions'] === true
        });
        if (workflow.idOwnerUser) this.resolveOwnerName(workflow.idOwnerUser);
        this.exitCriteriaRules  = Array.isArray(settings['exitCriteria'])     ? settings['exitCriteria']     : [];
        this.suppressionRulesArr = Array.isArray(settings['suppressionRules']) ? settings['suppressionRules'] : [];
        const rampUp     = settings['rampUp']      ?? {};
        const monitoring = settings['monitoring'] ?? {};
        this.form.patchValue({
          rampUpEnabled:                rampUp?.enabled      ?? false,
          rampUpPercentage:             rampUp?.percentage   ?? null,
          monitoringErrorAlertsEnabled: monitoring?.errorAlertsEnabled  ?? false,
          monitoringNotifyOwner:        monitoring?.notifyOwner          ?? true,
          monitoringNotifyAdmins:       monitoring?.notifyAdmins         ?? false,
          monitoringErrorThreshold:     monitoring?.errorThreshold       ?? null,
          monitoringErrorRateThreshold: monitoring?.errorRateThreshold   ?? null
        });

        // Pre-cargar nodos si la re-inscripción FROM_NODE ya está configurada
        if (settings['allowReEnrollment'] && settings['reEnrollmentStartMode'] === 'FROM_NODE') {
          this.loadNodes();
        }

        // INC_CRM_020 (6) inc15 — con el workflow activo la ficha sigue editable y solo se
        // congela lo que cambiaría la ejecución con inscripciones en vuelo. Los controles se
        // deshabilitan de verdad (no solo visualmente) para que no puedan enviarse; los payloads
        // se construyen con getRawValue(), así que su valor actual sigue viajando intacto.
        this.workflowStatus = workflow.status ?? null;
        if (this.lockedByActive) {
          this.lockExecutionFields();
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudo cargar el workflow.';
        this.loading = false;
      }
    });
  }

  private loadFolders(): void {
    this.folderApi.getFolders(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (folders) => { this.folders = folders.filter(f => f.isActive); },
      error: () => {}
    });
  }

  loadNodes(): void {
    if (!this.isEditing || !this.workflowId || this.nodesLoading) return;
    this.nodesLoading = true;
    this.builderApi.getGraph(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (graph) => {
        this.availableNodes = graph.nodes.filter(
          n => n.isActive && n.nodeType !== 'TRIGGER' && n.nodeType !== 'END' && n.nodeType !== 'ANNOTATION'
        );
        this.nodesLoading = false;
      },
      error: () => { this.nodesLoading = false; }
    });
  }

  getNodeDisplayLabel(node: WorkflowNodeResponse): string {
    const typeLabel = workflowNodeTypeLabel(node.nodeType, node.nodeType);
    const subtypeLabel = node.nodeSubtype ? ' · ' + workflowNodeSubtypeLabel(node.nodeSubtype) : '';
    return `${node.title || typeLabel + subtypeLabel} (${typeLabel})`;
  }

  private loadFromTemplate(templateId: number): void {
    this.loading = true;
    this.workflowApi.getTemplates(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (templates) => {
        const tpl = templates.find(t => t.id === templateId);
        if (tpl) {
          this.form.patchValue({
            name:        tpl.name,
            description: tpl.description ?? '',
            objectType:  tpl.objectType
          });
        }
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  // ── Tags ──────────────────────────────────────────────────────────────────

  get tags(): string[] {
    return this.form.get('tags')?.value ?? [];
  }

  addTag(): void {
    const tag = this.tagInput.trim();
    if (!tag || this.tags.includes(tag)) { this.tagInput = ''; return; }
    this.form.patchValue({ tags: [...this.tags, tag] });
    this.tagInput = '';
  }

  addTagOnEnter(event: Event): void {
    event.preventDefault();
    this.addTag();
  }

  removeTag(tag: string): void {
    this.form.patchValue({ tags: this.tags.filter(t => t !== tag) });
  }

  // ── Plantillas ────────────────────────────────────────────────────────────

  openTemplatesModal(): void {
    this.showTemplatesModal = true;
    this.templatesLoading = true;
    this.workflowApi.getTemplates(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (tpls) => { this.templates = tpls; this.templatesLoading = false; },
      error: () => { this.templatesLoading = false; }
    });
  }

  selectTemplate(template: WorkflowResponse): void {
    this.form.patchValue({
      name:        template.name,
      description: template.description ?? '',
      objectType:  template.objectType
    });
    this.showTemplatesModal = false;
  }

  createFromTemplate(template: WorkflowResponse): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    this.error = '';
    this.workflowApi.createFromTemplate(template.id, this.buildCreateRequest()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (created) => { this.router.navigate(['/automation/workflows', created.id, 'builder']); },
      error: (err) => { this.error = this.getWorkflowFormErrorMessage(err); this.saving = false; }
    });
  }

  // ── Guardar ───────────────────────────────────────────────────────────────

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const validationErr = this.validateBeforeSave();
    if (validationErr) { this.error = validationErr; return; }

    this.saving = true;
    this.error = '';

    // INC_CRM_020 (2) inc11 — guardar navegaba en silencio: sin toast ni banner, el
    // usuario no tenía confirmación de que se hubiera creado o actualizado nada.
    if (this.isEditing && this.workflowId) {
      this.workflowApi.update(this.workflowId, this.buildUpdateRequest()).pipe(takeUntil(this.destroy$)).subscribe({
        next: (updated) => {
          this.toast.updated('Workflow', `Se guardaron los cambios de "${updated.name}".`);
          // INC_CRM_020 (2) inc4 — el detalle ya no es una página: se vuelve al listado,
          // desde donde se abre el modal de detalle.
          this.router.navigate(['/automation/workflows']);
        },
        error: (err) => {
          this.error = this.getWorkflowFormErrorMessage(err);
          this.saving = false;
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.workflowApi.create(this.buildCreateRequest()).pipe(takeUntil(this.destroy$)).subscribe({
        next: (created) => {
          this.toast.created('Workflow', `"${created.name}" se creó como borrador. Ahora construye su flujo.`);
          this.router.navigate(['/automation/workflows', created.id, 'builder']);
        },
        error: (err) => {
          this.error = this.getWorkflowFormErrorMessage(err);
          this.saving = false;
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  private buildSettings(): Record<string, any> {
    const v = this.form.getRawValue();
    const reEnroll = v.allowReEnrollment ?? false;
    return {
      allowReEnrollment:       reEnroll,
      reEnrollmentStartMode:   reEnroll ? (v.reEnrollmentStartMode ?? 'FROM_START') : 'FROM_START',
      reEnrollmentStartNodeId: (reEnroll && v.reEnrollmentStartMode === 'FROM_NODE')
                                 ? (v.reEnrollmentStartNodeId ?? null)
                                 : null,
      exitCriteria:     this.exitCriteriaRules,
      suppressionRules: this.suppressionRulesArr,
      allowDangerousActions: v.allowDangerousActions ?? false,
      rampUp: v.rampUpEnabled
        ? { enabled: true, percentage: v.rampUpPercentage ?? 100, mode: 'PERCENTAGE' }
        : { enabled: false, percentage: 100, mode: 'PERCENTAGE' },
      monitoring: {
        errorAlertsEnabled:  v.monitoringErrorAlertsEnabled ?? false,
        notifyOwner:         v.monitoringNotifyOwner        ?? true,
        notifyAdmins:        v.monitoringNotifyAdmins       ?? false,
        errorThreshold:      v.monitoringErrorThreshold     ?? null,
        errorRateThreshold:  v.monitoringErrorRateThreshold ?? null
      }
    };
  }

  private buildCreateRequest(): WorkflowCreateRequest {
    const v = this.form.getRawValue();
    return {
      intIdBussines:        this.businessId,
      idFolder:             v.idFolder ?? null,
      name:                 v.name.trim(),
      description:          v.description?.trim() || null,
      objectType:           v.objectType,
      idOwnerUser:          v.idOwnerUser ?? null,
      tags:                 v.tags ?? [],
      settings:             this.buildSettings(),
      priorityOrder:        v.priorityOrder ?? 0,
      maxActiveEnrollments: v.maxActiveEnrollments ?? null
    };
  }

  /**
   * INC_CRM_020 (2) inc10 — `objectType` es `@NotBlank` en `WorkflowUpdateRequest` del
   * backend, pero este payload no lo incluía: TODA edición respondía 400 "objectType:
   * El objectType es obligatorio" aunque el desplegable "Tipo de objeto base" tuviera
   * valor. `buildCreateRequest()` sí lo mandaba; solo fallaba la ruta de edición.
   */
  private buildUpdateRequest(): WorkflowUpdateRequest {
    const v = this.form.getRawValue();

    // INC_CRM_020 (2) inc9 — un null suelto significa "no lo toques" en el backend, así que
    // vaciar un campo (p.ej. elegir "Sin carpeta") no hacía nada. Lo que el usuario dejó en
    // blanco se declara aquí para que se borre de verdad.
    const clearFields: string[] = [];
    if (v.idFolder == null)             clearFields.push('idFolder');
    if (v.idOwnerUser == null)          clearFields.push('idOwnerUser');
    if (v.maxActiveEnrollments == null) clearFields.push('maxActiveEnrollments');
    if (!v.description?.trim())         clearFields.push('description');

    return {
      idFolder:             v.idFolder ?? undefined,
      name:                 v.name.trim(),
      description:          v.description?.trim() || undefined,
      objectType:           v.objectType,
      idOwnerUser:          v.idOwnerUser ?? undefined,
      clearFields,
      tags:                 v.tags ?? [],
      settings:             this.buildSettings(),
      priorityOrder:        v.priorityOrder ?? 0,
      maxActiveEnrollments: v.maxActiveEnrollments ?? undefined
    };
  }

  private validateBeforeSave(): string | null {
    const v = this.form.getRawValue();
    if (v.allowReEnrollment && v.reEnrollmentStartMode === 'FROM_NODE' && !v.reEnrollmentStartNodeId) {
      return 'Debes seleccionar el paso de inicio para la re-inscripción.';
    }
    if (v.rampUpEnabled && (!v.rampUpPercentage || v.rampUpPercentage < 1 || v.rampUpPercentage > 100)) {
      return 'El porcentaje de activación gradual debe estar entre 1 y 100.';
    }
    if (v.monitoringErrorAlertsEnabled) {
      if (v.monitoringErrorThreshold !== null && v.monitoringErrorThreshold < 1) {
        return 'El umbral de errores debe ser mayor a 0.';
      }
      if (v.monitoringErrorRateThreshold !== null &&
          (v.monitoringErrorRateThreshold < 1 || v.monitoringErrorRateThreshold > 100)) {
        return 'El umbral de tasa de error debe estar entre 1 y 100.';
      }
    }
    return null;
  }

  // ── Error helpers ─────────────────────────────────────────────────────────

  private extractErrorCode(err: unknown): string {
    const e = err as any;
    const body = e?.error ?? {};
    return (
      body?.code ??
      body?.errorCode ??
      body?.details?.code ??
      body?.details?.errorCode ??
      (typeof body?.details === 'string' ? body.details : '') ??
      e?.code ??
      e?.errorCode ??
      ''
    );
  }

  getWorkflowFormErrorMessage(err: unknown): string {
    const code = this.extractErrorCode(err);
    switch (code) {
      case 'WORKFLOW_INVALID_EXIT_CRITERIA':
        return 'Hay un criterio de salida inválido. Revisa la configuración del workflow.';
      case 'WORKFLOW_INVALID_SUPPRESSION_RULE':
        return 'Hay una regla de supresión inválida. Revisa la configuración del workflow.';
      case 'WORKFLOW_INVALID_REENROLLMENT_START_MODE':
        return 'El modo de inicio de re-inscripción no es válido.';
      case 'WORKFLOW_REENROLLMENT_START_NODE_NOT_FOUND':
        return 'El nodo configurado para re-inscripción no existe en este workflow.';
      case 'WORKFLOW_RAMP_UP_INVALID_PERCENTAGE':
        return 'El porcentaje de activación gradual debe estar entre 1 y 100.';
      case 'WORKFLOW_ACTIVATION_CHECKLIST_FAILED':
        return 'No se puede guardar o activar el workflow porque falló la validación pre-activación.';
    }
    const e = err as any;
    const raw = e?.error?.message ?? e?.message;
    return raw ? this.humanizeBackendMessage(raw) : 'No se pudo guardar el workflow.';
  }

  /**
   * INC_CRM_020 (2) inc10 — el backend responde los errores de bean-validation con el
   * nombre INTERNO del campo por delante ("objectType: El objectType es obligatorio").
   * Eso no le dice nada al usuario y encima expone la nomenclatura del API, así que
   * aquí se traduce al rótulo que la pantalla muestra. Los estados internos
   * (DRAFT/PAUSED) reciben el mismo tratamiento.
   */
  private humanizeBackendMessage(message: string): string {
    const FIELD_LABELS: Record<string, string> = {
      name:                 'Nombre',
      description:          'Descripción',
      objectType:           'Tipo de objeto base',
      idFolder:             'Carpeta',
      idOwnerUser:          'Responsable',
      priorityOrder:        'Prioridad',
      maxActiveEnrollments: 'Máximo de inscripciones activas'
    };

    let texto = message.trim();

    // "objectType: El objectType es obligatorio" -> "El tipo de objeto base es obligatorio"
    const prefijo = texto.match(/^([a-zA-Z][a-zA-Z0-9]*): (.+)$/);
    if (prefijo) {
      const rotulo = FIELD_LABELS[prefijo[1]];
      if (rotulo) {
        const enFrase = rotulo.toLowerCase();
        const detalle = prefijo[2].replace(new RegExp(`\\b${prefijo[1]}\\b`, 'g'), enFrase);
        // El backend ya nombra el campo dentro del detalle ("El objectType es
        // obligatorio"), así que repetirlo como prefijo sobra.
        texto = detalle.toLowerCase().includes(enFrase) ? detalle : `${rotulo}: ${detalle}`;
      }
    }

    for (const [estado, rotulo] of Object.entries(WORKFLOW_STATUS_LABELS)) {
      texto = texto.replace(new RegExp(`\\b${estado}\\b`, 'g'), rotulo);
    }
    return texto;
  }

  // ── Helpers de reglas ─────────────────────────────────────────────────────

  valueRequiredForOperator(op: string): boolean {
    return op !== 'IS_EMPTY' && op !== 'IS_NOT_EMPTY';
  }

  /**
   * Etiqueta del operador. Desde que la lista se filtra por el tipo de la propiedad puede incluir
   * operadores que solo existen en el diccionario del constructor (BETWEEN, «Es posterior a»…);
   * sin esta segunda búsqueda la vista previa pintaba la clave cruda.
   */
  getOperatorLabel(op: string): string {
    const own = WORKFLOW_RULE_OPERATOR_LABELS[op as WorkflowRuleOperator];
    if (own) {
      return own;
    }
    return CONDITION_OPERATOR_OPTIONS.find((o) => o.value === op)?.label ?? op;
  }

  /**
   * Etiqueta legible de un tipo de objeto (`CONTACT` → «Contacto»). Diccionario único del módulo.
   */
  objectTypeText(objectType: string | null | undefined): string {
    return workflowObjectTypeLabel(objectType);
  }

  /**
   * Nombre legible de una propiedad del registro (`lifecycleStage` → «Ciclo de vida»). Sale del
   * catálogo de propiedades del builder; si la propiedad no está catalogada (atributo propio,
   * clave escrita a mano) se devuelve tal cual.
   */
  propertyText(objectType: string | null | undefined, field: string | null | undefined): string {
    const raw = (field ?? '').trim();
    if (!raw) {
      return '';
    }
    return findPropertyDescriptor(this.propertyOptionsFor(objectType), raw)?.label ?? raw;
  }

  /** Resumen legible de una regla, incluyendo listas y rangos (antes solo pintaba `value`). */
  getRulePreviewText(rule: { objectType?: string; field?: string; operator?: string;
                             value?: unknown; values?: unknown[]; from?: unknown; to?: unknown }): string {
    const obj = rule.objectType ? `${workflowObjectTypeLabel(rule.objectType, rule.objectType)}: ` : '';
    const operator = rule.operator ?? '';
    const opLabel = this.getOperatorLabel(operator).toLowerCase();
    const normalized = operator.trim().toUpperCase();
    let val = '';
    if (normalized === 'IN' || normalized === 'NOT_IN') {
      val = ` "${(rule.values ?? []).map((v) => this.asText(v)).join(', ')}"`;
    } else if (normalized === 'BETWEEN') {
      val = ` "${this.asText(rule.from)}" y "${this.asText(rule.to)}"`;
    } else if (this.valueRequiredForOperator(operator)) {
      val = ` "${this.asText(rule.value)}"`;
    }
    return `Si ${obj}${this.propertyText(rule.objectType, rule.field)} ${opLabel}${val}`;
  }

  private emptyExitCriteriaDraft() {
    return {
      objectType: '', field: '', operator: 'EQUALS' as string,
      value: '' as unknown, values: [] as unknown[], from: '' as unknown, to: '' as unknown,
      reason: ''
    };
  }

  private emptySuppressionDraft() {
    return {
      type: 'OBJECT' as 'OBJECT' | 'PROPERTY', objectType: '', objectId: null as number | null,
      field: '', operator: 'EQUALS' as string,
      value: '' as unknown, values: [] as unknown[], from: '' as unknown, to: '' as unknown,
      reason: ''
    };
  }

  /**
   * Operadores admitidos por la propiedad elegida, igual que en el constructor: una empresa o un
   * estado solo aceptan igualdad y pertenencia; una fecha, orden y rango. Ofrecer «mayor que» para
   * «Empresa» era una invitación a construir una regla que no puede cumplirse nunca.
   */
  ruleOperatorOptions(objectType: string | undefined, field: string, current: string) {
    const options = this.propertyOptionsFor(objectType);
    const descriptor = findPropertyDescriptor(options, field);
    return conditionOperatorOptionsFor(descriptor?.kind ?? 'TEXT', current);
  }

  /**
   * Mezcla en el borrador lo que emite `app-wf-condition-value`.
   *
   * El editor emite SOLO las claves de la forma activa (`{value}`, `{values}` o `{from,to}`), que
   * es justo lo que el motor lee: `inList` compara contra el array `values` y no parte un texto por
   * comas, así que un «En lista» escrito a mano nunca casaba.
   */
  applyRuleValue(draft: { value: unknown; values: unknown[]; from: unknown; to: unknown },
                 patch: Record<string, unknown>): void {
    if ('value' in patch) { draft.value = patch['value']; }
    if ('values' in patch) { draft.values = (patch['values'] as unknown[]) ?? []; }
    if ('from' in patch) { draft.from = patch['from']; }
    if ('to' in patch) { draft.to = patch['to']; }
  }

  /** Claves de comparación que hay que guardar según el operador. */
  private ruleValueParts(draft: { value: unknown; values: unknown[]; from: unknown; to: unknown },
                         operator: string): Record<string, unknown> {
    const normalized = (operator ?? '').trim().toUpperCase();
    if (normalized === 'IS_EMPTY' || normalized === 'IS_NOT_EMPTY') {
      return {};
    }
    if (normalized === 'IN' || normalized === 'NOT_IN') {
      return { values: draft.values ?? [] };
    }
    if (normalized === 'BETWEEN') {
      return { from: draft.from, to: draft.to };
    }
    return { value: typeof draft.value === 'string' ? draft.value.trim() : draft.value };
  }

  /** ¿El borrador tiene lo que su operador necesita? */
  private ruleValueMissing(draft: { value: unknown; values: unknown[]; from: unknown; to: unknown },
                           operator: string): boolean {
    const parts = this.ruleValueParts(draft, operator);
    if ('values' in parts) { return ((parts['values'] as unknown[]) ?? []).length === 0; }
    if ('from' in parts) { return this.asText(parts['from']) === '' || this.asText(parts['to']) === ''; }
    if ('value' in parts) { return this.asText(parts['value']) === ''; }
    return false;
  }

  /** A4: nombre visible del registro suprimido tras elegirlo en el buscador. */
  onSuppressionRecordSelected(item: unknown | null): void {
    this.draftSuppressionObjectName = item ? this.suppressionRecordSelect.label(item) : '';
  }

  // ── Exit Criteria ─────────────────────────────────────────────────────────

  openAddExitCriteria(): void {
    this.editingExitCriteriaIndex = null;
    this.draftExitCriteria = { ...this.emptyExitCriteriaDraft(), objectType: this.form.get('objectType')?.value ?? '' };
    this.draftExitCriteriaError = '';
    this.showAddExitCriteria = true;
  }

  startEditExitCriteria(index: number): void {
    const rule = this.exitCriteriaRules[index];
    this.editingExitCriteriaIndex = index;
    this.draftExitCriteria = {
      objectType: rule.objectType ?? '',
      field:      rule.field,
      operator:   rule.operator,
      value:      rule.value ?? '',
      values:     rule.values ?? [],
      from:       rule.from ?? '',
      to:         rule.to ?? '',
      reason:     rule.reason ?? ''
    };
    this.draftExitCriteriaError = '';
    this.showAddExitCriteria = true;
  }

  confirmAddExitCriteria(): void {
    const d = this.draftExitCriteria;
    if (!d.field.trim())  { this.draftExitCriteriaError = 'El campo es requerido.'; return; }
    if (!d.operator)      { this.draftExitCriteriaError = 'El operador es requerido.'; return; }
    if (this.ruleValueMissing(d, d.operator)) {
      this.draftExitCriteriaError = 'El valor es requerido para este operador.'; return;
    }
    const rule: WorkflowExitCriteriaRule = {
      type:       'PROPERTY',
      objectType: d.objectType.trim() || undefined,
      field:      d.field.trim(),
      operator:   d.operator as WorkflowRuleOperator,
      ...this.ruleValueParts(d, d.operator),
      reason:     d.reason.trim() || undefined
    };
    if (this.editingExitCriteriaIndex !== null) {
      this.exitCriteriaRules = this.exitCriteriaRules.map((r, i) =>
        i === this.editingExitCriteriaIndex ? rule : r
      );
      this.editingExitCriteriaIndex = null;
    } else {
      this.exitCriteriaRules = [...this.exitCriteriaRules, rule];
    }
    this.showAddExitCriteria = false;
    this.draftExitCriteriaError = '';
  }

  cancelEditExitCriteria(): void {
    this.showAddExitCriteria = false;
    this.editingExitCriteriaIndex = null;
    this.draftExitCriteriaError = '';
  }

  removeExitCriteriaRule(index: number): void {
    this.exitCriteriaRules = this.exitCriteriaRules.filter((_, i) => i !== index);
    if (this.editingExitCriteriaIndex === index) { this.cancelEditExitCriteria(); }
  }

  // ── Suppression Rules ─────────────────────────────────────────────────────

  openAddSuppression(): void {
    this.editingSuppressionIndex = null;
    this.draftSuppression = { ...this.emptySuppressionDraft(), objectType: this.form.get('objectType')?.value ?? '' };
    this.draftSuppressionObjectName = '';
    this.draftSuppressionError = '';
    this.showAddSuppression = true;
  }

  startEditSuppression(index: number): void {
    const rule = this.suppressionRulesArr[index];
    this.editingSuppressionIndex = index;
    this.draftSuppression = {
      type:       rule.type,
      objectType: rule.objectType ?? '',
      objectId:   rule.objectId ?? null,
      field:      rule.field ?? '',
      operator:   rule.operator ?? 'EQUALS',
      value:      rule.value ?? '',
      values:     rule.values ?? [],
      from:       rule.from ?? '',
      to:         rule.to ?? '',
      reason:     rule.reason ?? ''
    };
    this.draftSuppressionObjectName = '';
    this.draftSuppressionError = '';
    this.showAddSuppression = true;
  }

  confirmAddSuppression(): void {
    const d = this.draftSuppression;
    if (!d.objectType.trim()) { this.draftSuppressionError = 'El tipo de objeto es requerido.'; return; }
    let rule: WorkflowSuppressionRule;
    if (d.type === 'OBJECT') {
      // El input es type="number": Angular puede almacenar un number → normalizar a texto antes de validar.
      const objectIdStr = (d.objectId === null || d.objectId === undefined) ? '' : String(d.objectId).trim();
      if (!objectIdStr || isNaN(Number(objectIdStr))) {
        this.draftSuppressionError = 'El ID de objeto debe ser un número válido.'; return;
      }
      rule = { type: 'OBJECT', objectType: d.objectType.trim(), objectId: Number(objectIdStr), reason: d.reason.trim() || undefined };
    } else {
      if (!d.field.trim())  { this.draftSuppressionError = 'El campo es requerido.'; return; }
      if (!d.operator)      { this.draftSuppressionError = 'El operador es requerido.'; return; }
      if (this.ruleValueMissing(d, d.operator)) {
        this.draftSuppressionError = 'El valor es requerido para este operador.'; return;
      }
      rule = {
        type:       'PROPERTY',
        objectType: d.objectType.trim() || undefined,
        field:      d.field.trim(),
        operator:   d.operator as WorkflowRuleOperator,
        ...this.ruleValueParts(d, d.operator),
        reason:     d.reason.trim() || undefined
      };
    }
    if (this.editingSuppressionIndex !== null) {
      this.suppressionRulesArr = this.suppressionRulesArr.map((r, i) =>
        i === this.editingSuppressionIndex ? rule : r
      );
      this.editingSuppressionIndex = null;
    } else {
      this.suppressionRulesArr = [...this.suppressionRulesArr, rule];
    }
    this.showAddSuppression = false;
    this.draftSuppressionError = '';
  }

  cancelEditSuppression(): void {
    this.showAddSuppression = false;
    this.editingSuppressionIndex = null;
    this.draftSuppressionError = '';
  }

  removeSuppressionRule(index: number): void {
    this.suppressionRulesArr = this.suppressionRulesArr.filter((_, i) => i !== index);
    if (this.editingSuppressionIndex === index) { this.cancelEditSuppression(); }
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  /** Caracteres escritos en el nombre, para el contador del formulario. */
  get nameLength(): number {
    return (this.form.get('name')?.value ?? '').length;
  }

  /** Caracteres escritos en la descripción, para el contador del formulario. */
  get descriptionLength(): number {
    return (this.form.get('description')?.value ?? '').length;
  }

  /**
   * INC_CRM_020 (2) inc5 — la plantilla imprimía un único mensaje fijo ("El nombre es
   * obligatorio") para CUALQUIER error del campo, así que un nombre demasiado largo
   * —que falla `maxLength`, no `required`— acusaba al usuario de haber dejado el campo
   * vacío cuando lo tenía lleno. Ahora el mensaje se deriva del validador que falló.
   */
  getFieldError(field: string): string {
    const errors = this.form.get(field)?.errors;
    if (!errors) return '';

    const ETIQUETAS: Record<string, string> = {
      name:        'El nombre',
      description: 'La descripción',
      objectType:  'El tipo de objeto base'
    };
    const etiqueta = ETIQUETAS[field] ?? 'Este campo';

    if (errors['required'])  return `${etiqueta} es obligatorio.`;
    if (errors['maxlength']) {
      const max = errors['maxlength'].requiredLength;
      const actual = errors['maxlength'].actualLength;
      return `${etiqueta} no puede superar los ${max} caracteres (tiene ${actual}).`;
    }
    if (errors['minlength']) return `${etiqueta} debe tener al menos ${errors['minlength'].requiredLength} caracteres.`;
    if (errors['min'])       return `${etiqueta} debe ser mayor o igual a ${errors['min'].min}.`;
    if (errors['max'])       return `${etiqueta} debe ser menor o igual a ${errors['max'].max}.`;
    return `${etiqueta} no es válido.`;
  }
}
